import ICMWrapper from "@/app/components/icm-wrapper";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import Image, { StaticImageData } from "next/image";
import React, { useState } from "react";
import HowTo1 from "@/app/assets/images/how-to-1.png";
import HowTo2 from "@/app/assets/images/how-to-2.png";
import HowTo3 from "@/app/assets/images/how-to-3.png";
import { ICM_NAME_FULL } from "@/app/utils/constants";
import Apple from "@/app/assets/icons/apple";
import GooglePlay from "@/app/assets/icons/googleplay";
import GetICMAppModal from "@/app/components/get-icm-app-modal";

export interface HowToInvestStep {
    imageSrc: StaticImageData | string;
    title: string;
    description: React.ReactNode;
    imageAlt?: string;
    descriptionClassName?: string;
}

export interface HowToInvestProps {
    title?: string;
    steps?: HowToInvestStep[];
    backgroundImage?: string;
}

const defaultSteps: HowToInvestStep[] = [
    {
        imageSrc: HowTo1,
        title: "Create an account",
        description: "Sign up, complete your KYC",
        imageAlt: "Create an account",
    },
    {
        imageSrc: HowTo2,
        title: "Select & buy shares",
        description: "Choose from our curated shares",
        imageAlt: "Select & buy shares",
    },
    {
        imageSrc: HowTo3,
        title: "Receive your shares",
        description: (
            <>
                Share added to both your {ICM_NAME_FULL} portfolio and demat account.
            </>
        ),
        imageAlt: "See it in your account",
        descriptionClassName: "max-w-[350px]",
    },
];

interface StepProps {
    step: HowToInvestStep;
}

const Step: React.FC<StepProps> = ({ step }) => (
    <div className="flex-1">
        <div className="mb-7 rounded-[40px] bg-muted-blue-200">
            <Image src={step.imageSrc} alt={step.imageAlt ?? step.title} />
        </div>
        <div className="text-center text-white">
            <h5 className="text-xl font-semibold leading-[130%] tracking-[0%]">{step.title}</h5>
            <p
                className={`text-sm leading-[160%] tracking-[1%] ${step.descriptionClassName ?? ""}`}
            >
                {step.description}
            </p>
        </div>
    </div>
);

const DEFAULT_TITLE = `How to Invest with ${ICM_NAME_FULL}?`;
const DEFAULT_BACKGROUND = `url('${process.env.ICM_PUBLIC_ASSETS_URL}images/webp/how-to-invest.webp')`;

const HowToInvest: React.FC<HowToInvestProps> = ({ title, steps, backgroundImage }) => {
    const resolvedTitle = title ?? DEFAULT_TITLE;
    const resolvedSteps = steps ?? defaultSteps;
    const resolvedBackground = backgroundImage ?? DEFAULT_BACKGROUND;

    const [showAppModal, setShowAppModal] = useState<boolean>(false);

    const toggleDownloadApp = () => {
        setShowAppModal(!showAppModal);
    };

    return (
        <section
            className="bg-muted-blue-300 bg-[length:90%] bg-no-repeat py-s15 md:bg-[length:35%] md:py-s18"
            style={{
                backgroundImage: resolvedBackground,
            }}
        >
            <h2 className="mx-auto -mt-2 max-w-[80%] text-center text-4xl font-semibold leading-[130%] tracking-[-1%] text-white md:text-6xl">
                {resolvedTitle}
            </h2>

            <ICMWrapper>
                <div className="relative flex flex-col items-start justify-between gap-12 py-10 md:flex-row md:gap-20">
                    {resolvedSteps.map((step, index) => (
                        <Step key={index} step={step} />
                    ))}
                </div>

                <div className="flex flex-col items-center justify-center gap-4 md:flex-row">
                    <RegularButton
                        noIcon
                        showRightIcon={false}
                        size={ButtonSize.Large}
                        variant={ButtonVariant.SecondaryDefault}
                        label="Get Started"
                        className="mb-2 w-full max-w-full border-neutralOpacityLight-30 text-white hover:text-black md:mb-0 md:max-w-fit"
                        onClick={() =>
                            (window.location.href = String(process.env.ICM_URL) + "/login")
                        }
                    />
                    <RegularButton
                        id="download-app-button"
                        noIcon
                        showRightIcon={true}
                        rightIcon={
                            <div className="flex h-12 min-w-16 items-center justify-center gap-s4">
                                <Apple className="h-10 w-10 text-black" />
                                <div className="h-full w-[3px] bg-neutralOpacityDark-20"></div>
                                <GooglePlay className="h-10 w-10 text-black" />
                                <div className="absolute -top-[14px] right-7 rounded-full border border-orange-500 bg-orange-100 px-4 py-1 text-xs">
                                    Coming soon
                                </div>
                            </div>
                        }
                        size={ButtonSize.Large}
                        variant={ButtonVariant.SecondaryDefault}
                        label="Download the App"
                        className="relative mt-4 w-full max-w-full cursor-auto bg-white sm:mt-0 md:w-auto"
                        onClick={() => {}}
                    />
                </div>
            </ICMWrapper>

            <GetICMAppModal open={showAppModal} onClose={toggleDownloadApp} />
        </section>
    );
};

export default HowToInvest;
